import { useMemo } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon, IconName } from '@frontend/icons';
import { theme } from '@frontend/theme';

type StatusConfig = {
  label: string;
  iconName?: IconName;
  background: string;
  color: string;
};

type Props = {
  variant: 'ai-requested' | 'online-requested';
  iconOnly?: boolean;
};

export const StatusBadge = ({ variant, iconOnly = false }: Props) => {
  const { t } = useTranslation('schedule');

  const { label, iconName, background, color }: StatusConfig = useMemo(() => {
    switch (variant) {
      case 'ai-requested':
        return {
          label: t('AI-Requested'),
          iconName: 'aiassistant-small',
          background: `linear-gradient(100deg, ${theme.colorsV2.blue10()}, ${theme.colorsV2.seaweed5()})`,
          color: 'var(--fabric-color-black)',
        };
      case 'online-requested':
        return {
          label: t('Online Requested'),
          background: theme.colorsV2.indigo50(25),
          color: theme.colorsV2.indigo70(),
        };
      default:
        return {
          label: '',
          background: theme.colors.white,
          color: theme.colors.black,
        };
    }
  }, [variant, t]);

  const hasIconAndLabel = !!iconName && !iconOnly;

  return (
    <div css={statusBadgeStyles(background, color, hasIconAndLabel)}>
      {iconName && <Icon name={iconName} size={16} css={{ flexShrink: 0 }} />}
      {!iconOnly && <span>{label}</span>}
    </div>
  );
};

const statusBadgeStyles = (background: string, color: string, hasIconAndLabel: boolean) => css`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing(0.5)};
  background: ${background};
  color: ${color};
  padding: ${hasIconAndLabel ? theme.spacing(0.5) : theme.spacing(0.25, 0.5)};
  border-radius: ${theme.borderRadius.small};
  font-size: ${theme.font.size.small};
`;
